import { formatDistanceStrict } from 'date-fns'
import { fr } from 'date-fns/locale'
import { cn } from '#app/utils/misc.tsx'
import { type AgentState } from '#app/utils/support-center-api.server.ts'

/**
 * Presentation helpers for the consolidated agent state exposed by the
 * Support-center API (`offline | available | ringing | on_call`).
 */

export const AGENT_STATE_LABEL: Record<AgentState, string> = {
	offline: 'Hors ligne',
	available: 'Disponible',
	ringing: 'Sonne',
	on_call: 'En appel',
}

/** Twilio outcome of the last call, plus the two system-side releases. */
export const CALL_OUTCOME_LABEL: Record<string, string> = {
	completed: 'Terminé',
	'no-answer': 'Sans réponse',
	busy: 'Occupé',
	canceled: 'Annulé',
	failed: 'Échec',
	reconciled: 'Libéré automatiquement',
	manual: 'Libéré manuellement',
}

export function callOutcomeLabel(outcome: string | null): string | null {
	if (!outcome) return null
	return CALL_OUTCOME_LABEL[outcome] ?? outcome
}

export function agentStateDotClass(state: AgentState): string {
	switch (state) {
		case 'available':
			return 'bg-green-600'
		case 'ringing':
			return 'bg-sky-500 animate-pulse'
		case 'on_call':
			return 'bg-amber-500'
		case 'offline':
			return 'bg-muted-foreground/40'
	}
}

/** « 3 minutes », « 2 heures » — strict, no « environ ». */
export function formatSince(
	iso: string | null,
	now: string | Date,
): string | null {
	if (!iso) return null
	const from = new Date(iso)
	const to = typeof now === 'string' ? new Date(now) : now
	if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null
	return formatDistanceStrict(from, to, { locale: fr })
}

/**
 * Pill for a ringing / on-call agent with the elapsed time. Renders nothing
 * when the agent is idle — the status dot already says « disponible ».
 */
export function CallStateBadge({
	state,
	stateSince,
	now,
	className,
}: {
	state: AgentState
	stateSince: string | null
	now: string
	className?: string
}) {
	if (state !== 'ringing' && state !== 'on_call') return null
	const since = formatSince(stateSince, now)
	return (
		<span
			className={cn(
				'rounded-full px-2 py-0.5 font-medium',
				state === 'ringing'
					? 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300'
					: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
				className,
			)}
		>
			{AGENT_STATE_LABEL[state]}
			{since ? ` · ${since}` : ''}
		</span>
	)
}
